import { ArrowButtons, CarouselLayout } from '@shared/Frontend/components/carousel'
import { Clickable } from '@shared/Frontend/components/clickable'
import { Img } from '@shared/Frontend/components/img'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { pushDatalayer } from '@shared/Frontend/seo/datalayer'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { slugify } from '@shared/Frontend/utils/slugify'
import * as React from 'react'
import { useRef } from 'react'
import { GridBlock } from '../../blocks.state'

export const GridBlockLayout = (
  props: { id: string; enableCarousel?: boolean; viewportWidth?: number } & GridBlock
) => {
  const {
    id,
    items,
    bgColour,
    variant,
    intro,
    label,
    title,
    primaryUpdater,
    showUpdaterAsLink,
    enableCarousel,
    viewportWidth
  } = props
  const { inView, ref } = useInView({ threshold: 0.1 })

  const showCarousel =
    enableCarousel && ((items.length > 3 && variant == 'image') || (viewportWidth < 768 && items.length >= 3))
  const hasDoubleColumn = !showCarousel && items.length <= 2
  const SubHeading = title ? 'h3' : 'h2'
  const hasPrimaryUpdater = primaryUpdater && primaryUpdater.title && primaryUpdater.url

  const prevRef = useRef<HTMLButtonElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)
  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        `block block--linkgrid linkgrid--${variant} block-bg--${bgColour} ls-block-spacing`,
        showCarousel ? 'linkgrid--carousel' : 'linkgrid--plain',
        `linkgrid--count-${items.length}`,
        inView && 'inview'
      )}
    >
      <div className={cn('block__inner', !showCarousel && 'ls-container')}>
        {(label || title || intro || showCarousel) && (
          <header className={cn('block__header', showCarousel && 'ls-container')}>
            <div className="block__header-inner ls-reversed-column">
              {title && (
                <h2 id={slugify(title)} className="block__title title--l">
                  {title}
                </h2>
              )}
              {/* {label && <p className="block__label">{label}</p>} */}
            </div>
            {intro && <p className="block__intro">{intro}</p>}

            {showCarousel && (
              <div className="block__arrows">
                <ArrowButtons prevRef={prevRef} nextRef={nextRef} />
              </div>
            )}
          </header>
        )}

        <div className="block__content">
          <ListLayout
            items={items}
            variant={variant}
            subHeading={SubHeading}
            listClassName={cn('block__list grid', hasDoubleColumn ? 'grid--double-column' : 'grid--triple-column')}
            showCarousel={showCarousel}
            arrowsRef={{
              prev: prevRef,
              next: nextRef
            }}
          />
        </div>

        {hasPrimaryUpdater && (
          <footer className={cn('block__footer', showCarousel && 'ls-container')}>
            <div className="ls-clickables">
              {hasPrimaryUpdater && (
                <div className="ls-clickable">
                  <Clickable
                    elementKind="link"
                    styleKind={showUpdaterAsLink ? 'link' : 'button'}
                    {...primaryUpdater}
                    className="outline-button"
                    onClick={() =>
                      pushDatalayer({
                        category: 'TextBlock',
                        action: primaryUpdater.title
                      })
                    }
                  />
                </div>
              )}
            </div>
          </footer>
        )}
      </div>
    </section>
  )
}

export const ListLayout = (props: {
  items: GridBlock['items']
  variant: 'image' | 'text' | 'icon'
  subHeading: 'h2' | 'h3'
  showCarousel: boolean
  listClassName?: string
  arrowsRef?: {
    prev: React.RefObject<HTMLButtonElement>
    next: React.RefObject<HTMLButtonElement>
  }
}) => {
  const { listClassName, items, variant, subHeading, showCarousel, arrowsRef } = props
  if (items.length == 0) return null
  return showCarousel ? (
    <CarouselLayout
      arrowsRef={arrowsRef}
      items={items.map((item, index) => {
        return (
          <Item
            key={`${index}-${item.title}`}
            {...item}
            subHeading={subHeading}
            variant={variant}
            showCarousel={true}
          />
        )
      })}
    />
  ) : (
    <ul className={cn('grid', listClassName)}>
      {items.map((item, index) => {
        return (
          <Item
            key={`${index}-${item.title}`}
            {...item}
            subHeading={subHeading}
            variant={variant}
            showCarousel={false}
          />
        )
      })}
    </ul>
  )
}

const Item = (
  props: { showCarousel: boolean; subHeading: 'h2' | 'h3'; variant: GridBlock['variant'] } & GridBlock['items'][number]
) => {
  const { showCarousel } = props
  const { inView, ref } = useInView<HTMLLIElement>({ threshold: 0.2 })
  return showCarousel ? (
    <Article {...props} />
  ) : (
    <li ref={ref} className={cn('grid__item', inView && 'inview')}>
      <Article {...props} />
    </li>
  )
}

const Article = (props: { subHeading: 'h2' | 'h3'; variant: GridBlock['variant'] } & GridBlock['items'][number]) => {
  const { subHeading, image, intro, label, link, title, variant } = props
  const hasUpdater = link && link.url && link.title
  const SubHeading = subHeading
  return (
    <article className={cn('grid__article', hasUpdater && 'has-action')}>
      {/* NB: Van Oord doesn't use icon grid */}
      {/* {variant == 'icon' && icon && (
        <div className="grid__visual grid__visual--icon">
          <SVGIcon id={icon} className="grid__icon" size={80} />
        </div>
      )} */}

      {variant == 'image' && image && (
        <div className="grid__visual grid__visual--image">
          <Img {...image} loading="lazy" className="grid__image" width="400" height="225" />
        </div>
      )}

      {(label || title || intro || hasUpdater) && (
        <div className="grid__text">
          {(label || title) && (
            <header className="grid__header">
              <div className="grid__header-inner ls-reversed-column">
                {title && <SubHeading className="grid__title title--s">{title}</SubHeading>}
                {label && <p className="grid__label tag--m">{label}</p>}
              </div>
            </header>
          )}

          {intro && <p className="grid__intro">{intro}</p>}

          {hasUpdater && (
            <footer className="grid__footer ls-clickables">
              <div className="ls-clickable">
                <Clickable
                  elementKind="link"
                  styleKind="link"
                  {...link}
                  className="grid__clickable grid__clickable--primary link-chevron"
                  icon="chevron-right"
                  onClick={() =>
                    pushDatalayer({
                      category: `grid_with_${variant}_block`,
                      label: title || '',
                      action: 'grid_item_click'
                    })
                  }
                />
              </div>
            </footer>
          )}
        </div>
      )}
    </article>
  )
}
